'use client'

import {
  GoogleMapsPrediction,
  GoogleMapsStructuredPlace
} from '../interfaces/googleMapsInterfaces'
import { useEffect, useState, useMemo, useRef } from 'react'
import CoincidenceOption from './CoincidenceOption'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'

import { useAutocomplete } from '../hooks/useAutocomplete'
import { useAutocompleteStore } from '../store'
import { ILocation } from './Autocomplete'

let timeout: NodeJS.Timeout
let isTyping = false

interface Props {
  /**
   * This property indicates whether the CoincidencesList is visible.
   * @type {boolean}
   */
  isVisible: boolean

  /**
   * This is the searchText of the SearchField.
   * @type {string}
   */
  searchText: string

  /**
   * Indicates whether the street number must be validated.
   * @type {boolean}
   */
  validateStreetNumber?: boolean

  /**
   * Assigns default regions.
   * @type {string[]}
   */
  limitedRegions?: string[]

  /**
   * This is the types of the AutocompleteResults
   * @type {string[]}
   * @default ["geocode"]
   */
  placeTypes?: string[]

  /**
   * This is the className of the Container
   * @type {string}
   * */
  itemClassName?: string

  itemTextClassName?: string

  withRemarkSelected?: boolean

  remarkSelectedClassNames?: string

  focusOptionClassNames?: string

  selectedIndex: number

  totalZones: number

  showResults: boolean

  /**
   * @description locations to show in the results
   * @type {ILocation[]}
   */
  locations?: ILocation[]

  /**
   * This function is called when the match is selected.
   * @type {(place: GoogleMapsStructuredPlace) => void}
   */

  onSelected: (place: GoogleMapsStructuredPlace) => void

  /**
   * This function is called when the matches are loaded.
   * @type {(hasCoincidences: boolean) => void}
   * */
  onLoadedCoincidences: (hasCoincidences: boolean) => void

  /**
   * This function is called when the matches are loaded and set the total amount of matches.
   * @type {(totalCoincidences: number) => void}
   * */
  setTotalCoincidences: (totalCoincidences: number) => void

  onLoadedCoincidencesService: (isReady: boolean) => void
}

/**
 * This component is responsible for displaying the matches.
 * @param isVisible - This property indicates whether the CoincidencesList is visible.
 * @param searchText - This is the searchText of the SearchField.
 * @param validateStreetNumber - Indicates whether the street number must be validated.
 * @param defaultBounds - Assigns default limits.
 * @param limitedRegions - Assigns default regions.
 * @param placeTypes - This is the types of the AutocompleteResults
 * @param onSelected - This function is called when the match is selected.
 * @param onLoadedCoincidences - This function is called when the matches are loaded.
 * @param setTotalCoincidences - This function is called when the matches are loaded and set the total amount of matches.
 * @param itemClassName - the classname for the item
 * @param withRemarkSelected - boolean to remark a selected option with the searchText.
 * @returns {JSX.Element}
 * */
const CoincidencesList = (props: Props): JSX.Element => {
  const {
    isVisible,
    searchText,
    validateStreetNumber,
    limitedRegions,
    placeTypes = ['geocode'],
    itemClassName,
    withRemarkSelected,
    remarkSelectedClassNames,
    focusOptionClassNames,
    itemTextClassName,
    selectedIndex,
    totalZones,
    showResults,
    locations,
    onSelected,
    onLoadedCoincidences,
    setTotalCoincidences,
    onLoadedCoincidencesService
  } = props

  const [currenSelectedPlace, setCurrentSelectedPlace] =
    useState<GoogleMapsStructuredPlace | null>(null)

  const { totalSuggestionsZones, setTotalSuggestionsGoogle } =
    useAutocompleteStore()

  const { findCoincidences, selectPlace } = useAutocomplete()

  const [coincidences, setCoincidences] = useState<GoogleMapsPrediction[]>([])

  const searchTextPrevious = usePrevious(searchText)

  const searchTextRef = useRef(searchText)
  searchTextRef.current = searchText

  const { hasStreetNumber, isVisibleNotCoincidences } = useMemo(() => {
    return {
      hasStreetNumber: /\d/.test(searchText),
      isVisibleNotCoincidences:
        !coincidences.length && Boolean(searchText) && searchTextPrevious !== ''
    }
  }, [coincidences.length, searchText, searchTextPrevious])

  const loadCoincidences = async (params: { label: string }): Promise<void> => {
    onLoadedCoincidencesService(false)
    const { label } = params

    const { isSuccess, data } = await findCoincidences({
      label,
      validateStreetNumber,
      limitedRegions,
      placeTypes
    })

    if (isTyping) {
      return
    }

    if (!isSuccess || !data?.length) {
      setCoincidences([])
      onLoadedCoincidences(false)
      setTotalCoincidences(0)
      setTotalSuggestionsGoogle([])
      onLoadedCoincidencesService(true)
      return
    }
    const locationsNames = locations?.map((location) => location.searchAddress)
    const filterData =
      locations && locations?.length > 0
        ? data.filter(
            (coincidence) =>
              !locationsNames?.includes(coincidence.placePrediction.text.text)
          )
        : data
    const coincidencesNames = filterData.map(
      (coincidence) => coincidence.placePrediction.text.text
    )
    setCoincidences(filterData)
    setTotalSuggestionsGoogle(coincidencesNames)
    onLoadedCoincidences(true)
    setTotalCoincidences(filterData.length)
    onLoadedCoincidencesService(true)
  }

  /**
   * This function is responsible for handling the keydown event.
   * @param event - This is the event to handle.
   * @returns {Promise<void>}
   */
  const handleKeyDown = async (event: any): Promise<void> => {
    if (!isVisible || !showResults) {
      return
    }

    if (event.key === 'Enter' && selectedIndex >= totalZones) {
      const select = await selectPlace({
        prediction: coincidences[selectedIndex - totalZones]
      })

      if (!select) {
        return
      }

      setCurrentSelectedPlace(select)
      onSelected(select)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [coincidences, selectedIndex, totalZones])

  useEffect(() => {
    if (searchText.length < 4) {
      onLoadedCoincidences(false)
      setCoincidences([])
      setTotalCoincidences(0)
      setTotalSuggestionsGoogle([])
      return
    }

    if (searchText === currenSelectedPlace?.fullAddress) {
      return
    }

    isTyping = true

    clearTimeout(timeout)

    timeout = setTimeout(() => {
      isTyping = false
      if (searchText && searchTextRef.current === searchText) {
        loadCoincidences({ label: searchText })
      }
    }, 300)

    if (!searchText) {
      onLoadedCoincidences(false)
      setCoincidences([])
      setTotalCoincidences(0)
      setTotalSuggestionsGoogle([])
    }
  }, [searchText])
  const handleLocationsAndCoincidences = () => {
    if (!locations) {
      return
    }
    const locationsNames = locations?.map((location) => location.searchAddress)
    const filterData =
      locations && locations?.length > 0
        ? coincidences.filter(
            (coincidence) =>
              !locationsNames?.includes(coincidence.placePrediction.text.text)
          )
        : coincidences
    const coincidencesNames = filterData.map(
      (coincidence) => coincidence.placePrediction.text.text
    )
    setCoincidences(filterData)
    setTotalSuggestionsGoogle(coincidencesNames)
    setTotalCoincidences(filterData.length)
  }
  useEffect(() => {
    handleLocationsAndCoincidences()
  }, [locations])

  return (
    <>
      {isVisibleNotCoincidences && showResults ? (
        <p className="text-xs text-[#898CA8]">
          {validateStreetNumber && !hasStreetNumber
            ? 'Ingresa tu dirección completa para ver los resultados'
            : 'Sin sugerencias de ubicaciones'}
        </p>
      ) : null}

      {coincidences.length && showResults ? (
        <div>
          <p className="text-xs text-[#898CA8]">Ubicaciones</p>

          <div className="flex flex-col mt-2.5">
            {coincidences.map((coincidence, index) => {
              return (
                <CoincidenceOption
                  remarkSelectedClassNames={remarkSelectedClassNames}
                  focusOptionClassNames={focusOptionClassNames}
                  withRemarkSelected={withRemarkSelected}
                  searchText={searchText}
                  className={itemClassName}
                  itemTextClassName={itemTextClassName}
                  key={coincidence.placePrediction.placeId}
                  index={index + (totalSuggestionsZones.length ?? 0)}
                  selectedIndex={selectedIndex}
                  prediction={coincidence}
                  onSelected={(place) => {
                    setCurrentSelectedPlace(place)
                    onSelected(place)
                  }}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default CoincidencesList
